<template>
  <tr>
    <td>
      <table
        role="presentation"
        cellpadding="0"
        cellspacing="0"
        style="border-collapse: collapse; border-spacing: 0px"
        :align="align"
        border="0"
      >
        <tbody>
          <tr>
            <td :key="tdStyleKey" :style="tdStyle" @click="openOptions()">
              <div
                v-if="block.mode === 'dynamic'"
                data-testid="product-text"
                :style="divStyle"
              >
                {{ blockContent }}
              </div>
              <inline-text-editor
                v-else
                :key="typeName"
                v-model="blockContent"
                :has-font="
                  block.attributes[`${typeName}-font-family`] !== undefined
                "
                :product-size="true"
                :editor-style="divStyle"
                :toolbar="toolbar"
                :inline="true"
                @on-change="handler"
                @on-init="handler"
                @onBlur="checkIfContentIsEmpty"
              >
              </inline-text-editor>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import InlineTextEditor from "../elements/InlineTextEditor.vue";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useApiBlocksStore } from "@/stores/apiBlocks";
  import { useGeneralStore } from "@/stores/generalStore";
  import { StyleValue, computed, onMounted } from "vue";

  const props = defineProps({
    block: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    columnIndex: {
      type: Number,
      required: true,
    },
    sectionIndex: {
      type: Number,
      required: true,
    },
    blockIndex: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
  });

  const emit = defineEmits(["open-settings"]);

  const mjmlStore = useMjmlStore();
  const apiBlocksStore = useApiBlocksStore();
  const generalStore = useGeneralStore();

  const typeName = computed(() => {
    if (props.name === "priceBefore") {
      return "price-before";
    }
    if (props.name === "publishedat") {
      return "published-at";
    }
    if (props.name === "publishedby") {
      return "published-by";
    }
    return props.name;
  });

  const defaultValue = computed(() => {
    const blockPreperties = apiBlocksStore.blocks.get(props.block.blockName);
    if (!blockPreperties) {
      return "";
    }
    return blockPreperties.blockStructure[typeName.value].defaultValue;
  });

  const toolbar = computed(() => {
    return `bold italic underline strikethrough charmap removeformat code nonbreaking${
      generalStore.isRtlLanguage ? " ltr rtl" : ""
    }`;
  });

  const blockContent = computed({
    get() {
      if (!props.item[typeName.value]) return "";
      if (typeof props.item[typeName.value] !== "string")
        return props.item[typeName.value].toString();
      return props.item[typeName.value];
    },
    set(val: string) {
      mjmlStore.updateSpecificBlockItemAttribute({
        prop: typeName.value,
        value: val,
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
        itemIndex: props.index,
      });
    },
  });

  const align = computed(() => {
    return props.block.attributes[`${typeName.value}-align`];
  });

  const divStyle = computed<StyleValue>(() => {
    return {
      "word-break": "break-word",
      fontSize: props.block.attributes[`${typeName.value}-font-size`],
      fontFamily:
        props.block.attributes[`${typeName.value}-font-family`] ||
        "Helvetica, Arial, sans-serif",
      color: props.block.attributes[`${typeName.value}-color`],
      textDecoration:
        props.block.attributes[`${typeName.value}-text-decoration`],
      textTransform: props.block.attributes[`${typeName.value}-text-transform`],
      letterSpacing: props.block.attributes[`${typeName.value}-letter-spacing`],
      fontStyle: props.block.attributes[`${typeName.value}-font-style`],
      fontWeight: props.block.attributes[`${typeName.value}-font-weight`],
      textAlign: props.block.attributes[`${typeName.value}-align`],
      lineHeight: props.block.attributes[`${typeName.value}-line-height`] || 1,
    };
  });

  const tdStyle = computed<StyleValue>(() => {
    return {
      "word-break": "break-word",
      padding: props.block.attributes[`${typeName.value}-padding`],
      textAlign: props.block.attributes[`${typeName.value}-align`]
        ? props.block.attributes[`${typeName.value}-padding`]
        : "0px 0px 0px 0px",
    };
  });

  const tdStyleKey = computed(() => {
    return JSON.stringify(divStyle.value);
  });

  const prodstructure = computed(() => {
    if (props.block.attributes["prodstructure"]) {
      return props.block.attributes["prodstructure"];
    }

    return false;
  });

  function handler(event: any) {
    const rawContent = event.target.bodyElement.innerText;

    mjmlStore.updateBlockAttribute({
      prop: `raw-${typeName.value}`,
      value: rawContent,
      product: true,
      prodstructure: prodstructure.value,
      skipSnapshot: true,
    });
  }

  function openOptions() {
    emit("open-settings", typeName.value);
    let timeoutTime = 400;
    if (
      document.querySelector(
        `[data-testid="product-${props.name}-tab"] [data-testid="t-tab-toggle-open"] button`
      )
    ) {
      timeoutTime = 10;
    }
    setTimeout(() => {
      const openSetting: HTMLElement = document.querySelector(
        `[data-testid="product-${props.name}-tab"] [data-testid="t-tab-toggle-open"] button`
      ) as HTMLElement;

      if (openSetting && !openSetting.classList.contains("!outline")) {
        openSetting.click();
      }

      if (openSetting) {
        setTimeout(() => {
          (document.querySelector(".scroll-area") as HTMLElement).scrollTop =
            openSetting.offsetTop - openSetting.offsetHeight;
        }, 100);
      }
    }, timeoutTime);
  }

  function checkIfContentIsEmpty() {
    if (blockContent.value === "") {
      mjmlStore.updateSpecificBlockItemAttribute({
        prop: typeName.value,
        value: defaultValue.value,
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
        itemIndex: props.index,
      });
    }
  }

  onMounted(() => {
    checkIfContentIsEmpty();
  });
</script>
